import * as React from 'react';
import classNames from 'classnames';

import Icon from 'components/icon';

import { TempControlProject, TempControlProjectStatus } from '__generated-api__';

export type HistoryPanelLayout = 'desktop' | 'mobile';

interface HistoryPanelProps {
  /** Hydrated drafts (already filtered for stale rows by the hook). */
  entries: TempControlProject[];
  /** Currently-loaded draft - rendered with `is-selected` styling and not clickable. */
  activeProjectKey?: string;
  /** Click handler. The wizard re-uses its bootstrap path to swap drafts. */
  onSelect: (key: string) => void;
  /** Initial hydration in flight. We hide the panel during the very first */
  /** render to avoid flashing an empty list before keys arrive. */
  isLoading?: boolean;
  /**
   * When set, a "View all projects" link is rendered below the list.
   * The wizard passes the customer-portal index URL only for logged-in
   * users (anonymous visitors have no portal to send them to). Lives
   * outside the wizard's `BrowserRouter`, so this is an absolute URL
   * rendered via `<a href>`.
   */
  viewAllUrl?: string;
  /** Desktop history hides below `@md` and duplicates in the mobile row. */
  layout?: HistoryPanelLayout;
}

/**
 * "HISTORY" panel surfaced in the wizard's intro column on Step 1.
 *
 * Mirrors the design's `c-widget` + `c-tile` stacked-card look: an
 * outline-star bullet for drafts, a filled-star bullet for completed
 * sizings, the project name on the first line, a "Started on /
 * Finished on {date}" caption on the second line, and a chevron-right
 * affordance at the far right indicating the row is clickable.
 */
export const HistoryPanel: React.VFC<HistoryPanelProps> = ({
  entries,
  activeProjectKey,
  onSelect,
  isLoading,
  viewAllUrl,
  layout = 'desktop',
}) => {
  if (isLoading && entries.length === 0) return null;
  if (entries.length === 0) return null;

  const hasViewAll = typeof viewAllUrl === 'string' && viewAllUrl.length > 0;

  return (
    <div
      className={classNames(
        'c-widget c-widget--divider u-mt-spacer-base u-mt-spacer-base-large@lg',
        layout === 'desktop' && 'u-hidden u-block@md'
      )}
      aria-labelledby="tempcontrol-history-heading"
    >
      <p id="tempcontrol-history-heading" className="c-widget__headline u-mb-spacer-base-small">
        History
      </p>
      <div className="o-row c-tile__row u-mb-0">
        {entries.map((entry) => {
          const isActive = entry.key === activeProjectKey;
          return (
            <div key={entry.key} className="o-col-12">
              <HistoryTile entry={entry} isActive={isActive} onSelect={() => onSelect(entry.key)} />
            </div>
          );
        })}
      </div>
      {hasViewAll && (
        <p className="c-note">
          <a href={viewAllUrl}>View all projects</a>
        </p>
      )}
    </div>
  );
};

interface HistoryTileProps {
  entry: TempControlProject;
  isActive: boolean;
  onSelect: () => void;
}

const HistoryTile: React.VFC<HistoryTileProps> = ({ entry, isActive, onSelect }) => {
  const isCompleted = entry.status === TempControlProjectStatus.Completed;
  const statusLine = formatStatusLine(entry);
  const projectName = (entry.project_name && entry.project_name.trim()) || 'Untitled project';
  const iconName = isCompleted ? 'star-fill' : 'star';

  return (
    <article
      className={classNames('c-tile', 'c-tile--link', { 'is-selected': isActive })}
      aria-current={isActive ? 'true' : undefined}
    >
      <header className="c-tile__header">
        <Icon name={iconName} className="o-svg-icon" />
      </header>
      <div className="c-tile__body">
        <div className="c-tile__main">
          <div className="c-tile__label">
            <p>{statusLine}</p>
          </div>
          <div className="c-tile__title">
            <h6>
              {isActive ? (
                <span>{projectName}</span>
              ) : (
                <button type="button" className="c-tile__link" onClick={onSelect}>
                  {projectName}
                </button>
              )}
            </h6>
          </div>
        </div>
      </div>
      <footer className="c-tile__footer">
        <Icon name="chevron" className="o-svg-icon o-svg-right" />
      </footer>
    </article>
  );
};

/**
 * Build the "Started on / Finished on {date}" caption. Falls back to
 * a date-less variant if the relevant timestamp is missing on the
 * project (shouldn't happen in practice but keeps the UI robust).
 */
const formatStatusLine = (entry: TempControlProject): string => {
  const isCompleted = entry.status === TempControlProjectStatus.Completed;
  const raw = isCompleted ? entry.completed_at : entry.created_at;
  const formatted = formatDate(raw ?? null);
  if (isCompleted) {
    return formatted ? `Finished on ${formatted}` : 'Finished';
  }
  return formatted ? `Started on ${formatted}` : 'Started';
};

const formatDate = (raw: string | null): string | null => {
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

export default HistoryPanel;
